import type { Problem } from '@dashboard/contracts';

/** Error thrown for any non-2xx answer; carries the API problem document. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem,
  ) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
  }

  /** Field errors of a 400, keyed by field path. */
  get fieldErrors(): Record<string, string[]> {
    return this.problem.errors ?? {};
  }
}

type Query = Record<string, string | number | boolean | readonly (string | number)[] | null | undefined>;

export function buildUrl(path: string, query?: Query): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length > 0) params.set(key, value.join(','));
    } else {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return `/api${path}${qs ? `?${qs}` : ''}`;
}

let onUnauthorized: (() => void) | undefined;

/** Called once by the auth provider so an expired session sends the user back to the login page. */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function request<T>(
  method: string,
  path: string,
  options: { query?: Query; body?: unknown } = {},
): Promise<T> {
  const response = await fetch(buildUrl(path, options.query), {
    method,
    credentials: 'same-origin',
    headers:
      options.body === undefined
        ? { accept: 'application/json' }
        : { 'content-type': 'application/json', accept: 'application/json' },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    let problem: Problem;
    try {
      problem = (await response.json()) as Problem;
    } catch {
      problem = { type: 'about:blank', title: response.statusText || 'Erro', status: response.status };
    }
    if (response.status === 401 && !path.startsWith('/auth/login')) onUnauthorized?.();
    throw new ApiError(response.status, problem);
  }

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body }),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
};

/** Starts a file download (xlsx/csv) from a GET endpoint using the browser itself. */
export function download(path: string, query?: Query): void {
  const link = document.createElement('a');
  link.href = buildUrl(path, query);
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}
